import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { MessageSquareText, Pause, Phone, Play } from 'lucide-react'

import {
  getCallAudioUrl,
  listAuctionNotifyLog,
  type AuctionNotifyKind,
  type AuctionNotifyRow,
} from '@/api/auction-notify-log'
import { listAuctionSmsRecipients, type AuctionSmsRecipient } from '@/api/auction-sms'
import { PageHeader } from '@/components/layout/PageHeader'
import { Loader } from '@/components/ui/Loader'
import { SegmentedControl } from '@/components/ui/SegmentedControl'
import { TestBadge } from '@/components/inspections/LeadBadges'
import { emptyTableCellClassName } from '@/constants/ui'
import { dateFnsLocale } from '@/utils/dateLocale'
import { formatPhoneDisplay } from '@/utils/phone'

type KindFilter = 'all' | AuctionNotifyKind

const LIMIT = 200
const COLS = 5

/**
 * /sber-notify-log: журнал SMS и звонков дилерам о старте торгов. SMS из
 * sms_outbox (source auction_start), звонки из call_outbox с результатом,
 * длительностью и записью сообщения (bucket call-audio). Миграции stryzh
 * 20261103160000, 20261105220000. Тест из карточки лида (source auction_test,
 * 20261105234500) с меткой «ТЕСТ». Текст сообщения раскрывается щелчком по строке.
 */
export function SberNotifyLogPage() {
  const { t } = useTranslation()
  const [kind, setKind] = useState<KindFilter>('all')

  const logQ = useQuery({
    queryKey: ['auction-notify-log', kind],
    queryFn: () => listAuctionNotifyLog({ kind: kind === 'all' ? undefined : kind, limit: LIMIT }),
    refetchInterval: 15_000,
  })
  const rcpQ = useQuery({ queryKey: ['auction-sms-recipients'], queryFn: listAuctionSmsRecipients })
  const byPhone = useMemo(() => new Map((rcpQ.data ?? []).map((r) => [r.phone, r])), [rcpQ.data])

  return (
    <div className="w-full max-w-none px-6 py-4 lg:px-8">
      <PageHeader title={t('logs.sberNotify.title')} />

      <div className="mb-4">
        <SegmentedControl<KindFilter>
          aria-label={t('logs.sberNotify.kindLabel')}
          value={kind}
          onChange={setKind}
          options={[
            { value: 'all', label: t('logs.sberNotify.kindAll') },
            { value: 'sms', label: t('logs.sberNotify.kindSms') },
            { value: 'call', label: t('logs.sberNotify.kindCall') },
          ]}
        />
      </div>

      {logQ.error ? (
        <div className="mb-4 rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">
          {(logQ.error as Error).message}
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-xl border border-[var(--stryzh-color-border-default)]">
        <table className="w-full min-w-[820px] text-left text-sm">
          <thead className="bg-[var(--stryzh-color-surface)] text-xs text-[var(--stryzh-color-text-secondary)]">
            <tr>
              <th className="px-3 py-2 font-medium whitespace-nowrap">{t('logs.sberNotify.colWhen')}</th>
              <th className="px-3 py-2 font-medium whitespace-nowrap">{t('logs.sberNotify.colRecipient')}</th>
              <th className="px-3 py-2 font-medium whitespace-nowrap">{t('logs.sberNotify.colLot')}</th>
              <th className="px-3 py-2 font-medium whitespace-nowrap">{t('logs.sberNotify.colOutcome')}</th>
              <th className="w-12 px-3 py-2" aria-label={t('logs.sberNotify.colAudio')} />
            </tr>
          </thead>
          <tbody>
            {logQ.isLoading ? (
              <tr>
                <td colSpan={COLS} className={emptyTableCellClassName}>
                  <Loader centered size="sm" />
                </td>
              </tr>
            ) : !logQ.data || logQ.data.length === 0 ? (
              <tr>
                <td colSpan={COLS} className={emptyTableCellClassName}>
                  {t('common.noData')}
                </td>
              </tr>
            ) : (
              logQ.data.map((row) => (
                <LogRow key={`${row.kind}-${row.id}`} row={row} recipient={byPhone.get(row.phone) ?? null} />
              ))
            )}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-xs text-[color:var(--stryzh-color-text-secondary)]">
        {t('logs.sberNotify.footer', { n: LIMIT })}
      </p>
    </div>
  )
}

const STATUS_TONE: Record<string, string> = {
  sent: 'text-green-300',
  answered: 'text-green-300',
  queued: 'text-blue-300',
  calling: 'text-blue-300',
  no_answer: 'text-yellow-300',
  busy: 'text-yellow-300',
  rejected: 'text-yellow-300',
  failed: 'text-red-300',
}

const muted = 'text-[color:var(--stryzh-color-text-secondary)]'

function LogRow({ row, recipient }: { row: AuctionNotifyRow; recipient: AuctionSmsRecipient | null }) {
  const { t } = useTranslation()
  const locale = dateFnsLocale()
  const [textOpen, setTextOpen] = useState(false)
  const at = format(new Date(row.at), 'd MMM, HH:mm', { locale })
  // Поставлен вне окна звонков: показываем, с какого времени ждёт.
  const waiting =
    row.kind === 'call' && row.status === 'queued' && row.notBefore != null && Date.parse(row.notBefore) > Date.parse(row.at) + 60_000
  const car = row.lead
    ? [row.lead.vehicle_brand, row.lead.vehicle_model, row.lead.vehicle_year].filter((x) => x != null && x !== '').join(' ')
    : ''
  const note = waiting
    ? t('logs.sberNotify.waitWindow', { at: format(new Date(row.notBefore as string), 'd MMM, HH:mm', { locale }) })
    : [
        row.durationSec != null ? t('logs.sberNotify.talk', { d: formatDuration(row.durationSec) }) : null,
        // Робот пишет причину вместе с кодом («486 Busy Here»): код отдельно, только если причины нет.
        row.detail ?? (row.sipCode != null ? `SIP ${row.sipCode}` : null),
      ]
        .filter(Boolean)
        .join(' · ')
  const KindIcon = row.kind === 'call' ? Phone : MessageSquareText

  return (
    <>
      <tr
        onClick={() => setTextOpen((v) => !v)}
        title={textOpen ? undefined : t('logs.sberNotify.showText')}
        className="cursor-pointer border-t border-[color:var(--stryzh-color-border-default)] align-top hover:bg-[color:var(--stryzh-color-surface)]"
      >
        <td className={`px-3 py-2.5 whitespace-nowrap tabular-nums ${muted}`}>{at}</td>
        <td className="px-3 py-2.5 whitespace-nowrap">
          <div>{recipient?.full_name ?? formatPhoneDisplay('+' + row.phone)}</div>
          {recipient ? (
            <div className={`text-xs ${muted}`}>
              {recipient.dealer} · <span className="tabular-nums">{formatPhoneDisplay('+' + row.phone)}</span>
            </div>
          ) : null}
        </td>
        <td className="px-3 py-2.5 whitespace-nowrap">
          {row.lead ? (
            <Link to={`/inspection-leads/${row.lead.id}`} onClick={(e) => e.stopPropagation()} className="hover:underline">
              {row.lead.lead_no != null ? <span className={`mr-1.5 tabular-nums ${muted}`}>№{row.lead.lead_no}</span> : null}
              <span className="text-[color:var(--stryzh-color-accent-orange)]">{car || row.lead.id.slice(0, 8)}</span>
            </Link>
          ) : null}
        </td>
        <td className="px-3 py-2.5">
          <div className="flex items-center gap-2 whitespace-nowrap">
            <KindIcon size={14} aria-label={t(`logs.sberNotify.kind.${row.kind}`)} className={`shrink-0 ${muted}`} />
            <span className={STATUS_TONE[row.status] ?? muted}>{t(`logs.sberNotify.status.${row.kind}.${row.status}`)}</span>
            {row.isTest ? <TestBadge /> : null}
          </div>
          {note ? (
            <div className={`mt-0.5 max-w-[420px] pl-[22px] text-xs ${muted}`}>
              {note}
            </div>
          ) : null}
        </td>
        <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
          {row.audioPath ? <AudioButton path={row.audioPath} /> : null}
        </td>
      </tr>
      {textOpen ? (
        <tr className="bg-[color:var(--stryzh-color-surface)]">
          <td colSpan={COLS} className="px-3 pb-3 pt-1 text-sm">
            {row.message}
          </td>
        </tr>
      ) : null}
    </>
  )
}

/** 75 → «1:15». */
function formatDuration(sec: number): string {
  const s = Math.max(0, Math.round(sec))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/** Запись сообщения: ссылка на файл запрашивается по первому нажатию, дальше пауза и повтор. */
function AudioButton({ path }: { path: string }) {
  const { t } = useTranslation()
  const audio = useRef<HTMLAudioElement | null>(null)
  const [playing, setPlaying] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => () => audio.current?.pause(), [])

  const toggle = async () => {
    setError(null)
    try {
      if (!audio.current) {
        setLoading(true)
        const a = new Audio(await getCallAudioUrl(path))
        a.onended = () => setPlaying(false)
        audio.current = a
      }
      if (audio.current.paused) {
        await audio.current.play()
        setPlaying(true)
      } else {
        audio.current.pause()
        setPlaying(false)
      }
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  }

  const label = t(playing ? 'logs.sberNotify.pause' : 'logs.sberNotify.play')
  return (
    <button
      type="button"
      onClick={() => void toggle()}
      disabled={loading}
      aria-label={label}
      title={error ?? label}
      className={`inline-flex h-7 w-7 items-center justify-center rounded-full border transition-colors disabled:opacity-50 ${
        error
          ? 'border-red-500/50 text-red-300'
          : 'border-[color:var(--stryzh-color-border-default)] text-[color:var(--stryzh-color-accent-orange)] hover:border-[color:var(--stryzh-color-accent-orange)]'
      }`}
    >
      {playing ? <Pause size={13} aria-hidden /> : <Play size={13} aria-hidden className="translate-x-px" />}
    </button>
  )
}
